import { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { Building2, MapPinned, Newspaper, Camera, BookOpen, MapPin } from "lucide-react";
import api from "../lib/api";
import { fetchGuide } from "../lib/guide";
import { Spinner, IataBadge } from "../components/common";
import { pickAirportBg, CITY_SEED_PHOTOS } from "../lib/assets";
import SectionTabs from "../components/SectionTabs";
import Breadcrumbs from "../components/Breadcrumbs";
import GuideSections from "../components/GuideSections";
import NewsFeed from "../components/NewsFeed";
import PhotoGallery from "../components/PhotoGallery";
import DiscussionBoard from "../components/DiscussionBoard";

const TABS = [
  { id: "guide", label: "Guide", icon: BookOpen },
  { id: "airports", label: "Airports", icon: Building2 },
  { id: "cities", label: "Cities", icon: MapPinned },
  { id: "news", label: "News", icon: Newspaper },
  { id: "photos", label: "Photos", icon: Camera },
];

export default function StateDetail() {
  const { code } = useParams();
  const { hash } = useLocation();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [guide, setGuide] = useState(null);
  const [guideLoading, setGuideLoading] = useState(true);
  const [tab, setTab] = useState(() => {
    const h = (hash || "").replace("#", "");
    return TABS.some((t) => t.id === h) ? h : "guide";
  });

  useEffect(() => {
    setD(null); setErr(false); setGuide(null); setGuideLoading(true);
    api.get(`/regions/${encodeURIComponent(code)}`).then((r) => setD(r.data)).catch(() => setErr(true));
  }, [code]);

  useEffect(() => {
    if (!d) return;
    let alive = true;
    setGuideLoading(true);
    fetchGuide({ country: d.country_name, state: d.state_name })
      .then((g) => { if (alive) setGuide(g); })
      .finally(() => { if (alive) setGuideLoading(false); });
    return () => { alive = false; };
  }, [d]);

  if (err) return <div className="max-w-3xl mx-auto px-5 py-24 text-center text-muted-foreground">Region not found.</div>;
  if (!d) return <Spinner full />;

  const HDR = pickAirportBg(d.state_name);
  const hasGuide = guide && guide.sections && Object.keys(guide.sections).length > 0;
  const tabs = TABS.filter((t) => t.id !== "guide" || guideLoading || hasGuide);
  const activeTab = tabs.some((t) => t.id === tab) ? tab : "airports";

  return (
    <div data-testid="state-detail">
      <section className="relative border-b border-zinc-200">
        <div className="absolute inset-0"><img src={HDR} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-teal-900/75" /></div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-14">
          <Breadcrumbs className="text-white mb-3" items={[
            { label: "Countries", to: "/countries" },
            { label: d.country_name, to: `/countries/${d.country_code}` },
            { label: d.state_name },
          ]} />
          <div className="text-xs font-mono uppercase tracking-widest text-blue-300 mb-2">State / Region Guide</div>
          <h1 className="font-heading font-900 text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white">{d.state_name}</h1>
          <div className="flex items-center gap-1.5 text-zinc-200 mt-2 text-lg font-500"><MapPin className="w-5 h-5" />{d.country_name} · {d.airports.length} airports · {d.cities.length} cities</div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="pt-6"><SectionTabs tabs={tabs} active={activeTab} onChange={setTab} testid="state-tab" /></div>

        <div className="py-8 min-h-[280px]">
          {activeTab === "guide" && (
            <div data-testid="state-guide-panel">
              <h2 className="font-heading font-900 text-blue-600 text-2xl mb-5 flex items-center gap-2"><BookOpen className="w-6 h-6" /> About {d.state_name}</h2>
              {guideLoading ? <Spinner /> : hasGuide ? <GuideSections guide={guide} /> : <p className="text-sm text-muted-foreground">No detailed guide available for {d.state_name} yet.</p>}
            </div>
          )}

          {activeTab === "airports" && (
            <div data-testid="state-airports-panel">
              <h2 className="font-heading font-900 text-blue-600 text-2xl mb-5 flex items-center gap-2"><Building2 className="w-6 h-6" /> Airports in {d.state_name}</h2>
              {d.airports.length ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {d.airports.map((a) => (
                    <Link key={a.id} to={`/airports/${a.iata_code || a.id}`} data-testid={`state-airport-${a.id}`}
                      className="group border border-zinc-300 bg-white overflow-hidden hover:-translate-y-px hover:border-primary transition-[transform,border-color]">
                      <IataBadge code={a.iata_code || a.icao_code || "—"} name={a.name} className="h-28" />
                      <div className="p-4 text-sm text-muted-foreground capitalize">{(a.type || "").replace("_", " ")}{a.municipality ? ` · ${a.municipality}` : ""}</div>
                    </Link>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">No airports listed.</p>}
            </div>
          )}

          {activeTab === "cities" && (
            <div data-testid="state-cities-panel">
              <h2 className="font-heading font-900 text-violet-600 text-2xl mb-5 flex items-center gap-2"><MapPinned className="w-6 h-6" /> Cities in {d.state_name}</h2>
              {d.cities.length ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {d.cities.map((ct) => (
                    <Link key={ct.name} to={`/cities/${encodeURIComponent(ct.name)}?country=${d.country_code}&region=${d.region_code}`} data-testid={`state-city-${ct.name}`}
                      className="group border border-zinc-300 bg-white p-4 flex items-center gap-2 hover:-translate-y-px hover:border-primary transition-[transform,border-color]">
                      <MapPin className="w-4 h-4 text-primary shrink-0" />
                      <span className="font-600 text-sm truncate">{ct.name}</span>
                    </Link>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">No cities listed.</p>}
            </div>
          )}

          {activeTab === "news" && (
            <div data-testid="state-news-panel">
              <h2 className="font-heading font-900 text-cyan-600 text-2xl mb-5 flex items-center gap-2"><Newspaper className="w-6 h-6" /> {d.state_name} travel news</h2>
              <NewsFeed path={`/news?category=airport&q=${encodeURIComponent(d.state_name)}&limit=8&archive_as=state`} />
            </div>
          )}

          {activeTab === "photos" && (
            <div data-testid="state-photos-panel">
              <h2 className="font-heading font-900 text-emerald-600 text-2xl mb-5 flex items-center gap-2"><Camera className="w-6 h-6" /> {d.state_name} photos</h2>
              <PhotoGallery entityType="region" entityId={d.region_code} seedPhotos={CITY_SEED_PHOTOS} />
            </div>
          )}
        </div>

        <SectionTabs tabs={tabs} active={activeTab} onChange={setTab} testid="state-tab-bottom" />

        <div className="py-12">
          <DiscussionBoard entityType="region" entityId={d.region_code} title={`${d.state_name} discussion board`} />
        </div>
      </div>
    </div>
  );
}
